import { Analytics, reviewSourceFor, toTransactionSnapshot } from '@easy-expense/analytics-client';
import { currentUser } from '@easy-expense/auth-client';
import {
  currentUserEntityFields,
  updateReportsEntities,
  getCachedReports,
  useWorkspaceSettings,
  archiveReviewTransaction,
  hasAlreadyBeenReviewed,
  useWorkspaceKeysStore,
} from '@easy-expense/data-firestore-client';
import {
  Category,
  ExpenseSchema,
  getExpensePath,
  ReviewableTransaction,
  ReviewEmailExpense,
  TaxRate,
} from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer, zIndex } from '@easy-expense/ui-web-core';
import { determineNearestTax, getDefaultReimbursableFields } from '@easy-expense/utils-shared';
import _ from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { CurrencyField } from './CurrencyInput.component';
import { DateField } from './DateField.component';
import { NotesField } from './NotesField.component';
import { ReceiptViewer } from './ReceiptViewer.component';
import { ReceiptsField } from './ReceiptsField.component';
import { ReportsField } from './ReportsField.component';
import { TipField } from './TipField.component';
import { TransactionNumberField } from './TransactionNumberField.component';
import {
  MeteredPaywallActions,
  useHandleMeteredAction,
} from '../../../hooks/useHandleMeteredAction';
import { Button } from '../../Button.components';
import LoadingSpinner from '../../LoadingSpinner.component';
import { CloseButton } from '../../Shared/CloseButton.component';
import { InsertDropdown } from '../../Shared/Dropdown/InsertableDropdown.component';
import { SearchDropdown } from '../../Shared/Dropdown/SearchDropDown.component';
import { isTipEligibleCategoryKey } from '../helpers';

export const ReviewTransactionEdit: React.FC<{
  reviewExpense?: ReviewableTransaction;
  next: () => void;
}> = ({ reviewExpense, next }) => {
  const loadedExpense = reviewExpense?.data;
  const [expense, setExpense] = React.useState<Partial<ReviewEmailExpense>>({});
  const [reportKeys, setReportKeys] = React.useState<string[]>([]);
  const [errors, setErrors] = React.useState<{ [k: string]: string[] }>();
  const allVendors = Data.vendors.use();
  const categories: Category[] = Data.expenseCategories.use();
  const categoryOptions = React.useMemo(
    () =>
      categories.map((category) => {
        const glCode = category.value.glCode;
        const displayName = glCode ? `${category.value.name} - ${glCode}` : category.value.name;
        return {
          key: category.key,
          value: {
            ...category.value,
            displayName,
          },
        };
      }),
    [categories],
  );

  const allPaymentMethods = Data.paymentMethods.use();
  const paymentMethods = React.useMemo(
    () => allPaymentMethods.filter((pm) => pm.entityState === 'active'),
    [allPaymentMethods],
  );
  const navigate = useNavigate();
  const handleMeteredReview = useHandleMeteredAction(
    MeteredPaywallActions.REVIEW_EMAIL_TRANSACTION,
  );
  const [selectedReceiptIndex, setSelectedReceiptIndex] = React.useState<number>(0);
  const [loading, setLoading] = React.useState(false);
  const [newVendorName, setNewVendorName] = React.useState<string>();
  const [paymentMethodKey, setPaymentMethodKey] = React.useState<string | undefined>();
  const [newPaymentMethodName, setNewPaymentMethodName] = React.useState<string>();

  const paymentMethodOptions = React.useMemo(
    () => paymentMethods.map((pm) => ({ key: pm.key, value: { ...pm.value, color: null } })),
    [paymentMethods],
  );
  const pmUserSelectedRef = React.useRef(false);
  const vendorUserSelectedRef = React.useRef(false);
  const categoryUserSelectedRef = React.useRef(false);
  const screenWidth = window.innerWidth > 0 ? window.innerWidth : screen.width;
  const { workspaceSettings } = useWorkspaceSettings();
  const [updatedTotal, setUpdatedTotal] = React.useState<number>();
  const [updatedTax, setUpdatedTax] = React.useState<number>();
  const [updatedSecondTax, setUpdatedSecondTax] = React.useState<number>();
  const showTipField = isTipEligibleCategoryKey(expense.category);

  const [taxRate, secondTaxRate]: TaxRate[] | undefined = React.useMemo(() => {
    const trackedTaxRates = _.filter(workspaceSettings?.taxRates, (tr) => tr.tracked);
    return trackedTaxRates;
  }, [workspaceSettings]);

  React.useEffect(() => {
    if (!loadedExpense || loadedExpense.entityType !== 'reviewEmailExpense') {
      return;
    }
    const vendor =
      allVendors.find((v) => v.value.name === loadedExpense.vendor)?.key ?? 'newVendor';
    setNewVendorName(vendor === 'newVendor' ? loadedExpense.vendor || undefined : undefined);
    const category = categories.find((c) => c.value.name === loadedExpense.category)?.key;
    setExpense({ ...loadedExpense, vendor, category });
    setUpdatedTotal(loadedExpense.total);
  }, [loadedExpense]);

  React.useEffect(() => {
    if (!loadedExpense || loadedExpense.entityType !== 'reviewEmailExpense') {
      return;
    }
    const taxes = [loadedExpense.tax ?? 0, loadedExpense.secondTax ?? 0];
    if (taxRate) {
      setUpdatedTax(determineNearestTax(taxes, loadedExpense.total, taxRate.rate));
    } else {
      setUpdatedTax(undefined);
    }
    if (secondTaxRate) {
      setUpdatedSecondTax(determineNearestTax(taxes, loadedExpense.total, secondTaxRate.rate));
    } else {
      setUpdatedSecondTax(undefined);
    }
  }, [loadedExpense, taxRate, secondTaxRate]);

  const prevLoadedExpenseRef = React.useRef(loadedExpense);
  React.useEffect(() => {
    if (!loadedExpense || loadedExpense.entityType !== 'reviewEmailExpense') {
      return;
    }

    const loadedExpenseChanged = prevLoadedExpenseRef.current !== loadedExpense;
    if (loadedExpenseChanged) {
      prevLoadedExpenseRef.current = loadedExpense;
      vendorUserSelectedRef.current = false;
      categoryUserSelectedRef.current = false;
      pmUserSelectedRef.current = false;
      setPaymentMethodKey(undefined);
      setNewPaymentMethodName(undefined);
    }

    if (!vendorUserSelectedRef.current) {
      const foundVendor = allVendors.find((v) => v.value.name === loadedExpense.vendor);
      if (foundVendor) {
        setExpense((prev) => ({ ...prev, vendor: foundVendor.key }));
        setNewVendorName(undefined);
      }
    }

    if (!categoryUserSelectedRef.current) {
      const foundCategory = categories.find((c) => c.value.name === loadedExpense.category);
      if (foundCategory) {
        setExpense((prev) => ({ ...prev, category: foundCategory.key }));
      }
    }

    if (!pmUserSelectedRef.current) {
      const existingPaymentMethod = paymentMethods.find(
        (pm) => pm.key === loadedExpense.paymentMethod,
      );
      if (existingPaymentMethod) {
        setPaymentMethodKey(existingPaymentMethod.key);
        setNewPaymentMethodName(undefined);
        pmUserSelectedRef.current = true;
      } else if (loadedExpense.suggestion?.paymentMethod?.name) {
        const matchedByName = paymentMethods.find(
          (pm) => pm.value.name === loadedExpense.suggestion?.paymentMethod?.name,
        );
        if (matchedByName) {
          setPaymentMethodKey(matchedByName.key);
          setNewPaymentMethodName(undefined);
          pmUserSelectedRef.current = true;
        }
      }
    }
  }, [loadedExpense, allVendors, categories, paymentMethods]);

  function onMobile() {
    if (screenWidth < 768) {
      if (
        window.confirm(
          'The Experience is better on the App! Press "OK" to download the app. "CANCEL" will load this website ',
        )
      ) {
        window.location.href = 'https://app.easy-expense.com/app-download';
      }
    }
  }

  React.useEffect(() => {
    onMobile();
  }, []);

  function saveReviewedTransaction() {
    // the review list here is captured when the page loads, so a receipt saved moments
    // ago can still be on screen -- saving it again would file a second expense
    if (reviewExpense && hasAlreadyBeenReviewed(reviewExpense)) {
      next();
      return;
    }

    if (loadedExpense) {
      Analytics.track('transaction_reviewed', {
        ...toTransactionSnapshot(loadedExpense),
        action: 'saved',
        review_source: reviewSourceFor(loadedExpense),
        review_surface: 'web',
      });
    }
    setErrors({});

    if (!expense.date) {
      setErrors((errors) => ({ ...errors, date: ['invalid date'] }));
      return;
    }

    try {
      const vendorKeyForValidation =
        expense.vendor === 'newVendor' && !newVendorName
          ? null
          : (newVendorName ?? expense.vendor ?? null);

      const parsedExpense = ExpenseSchema.safeParse({
        ...expense,
        ...currentUserEntityFields('create'),
        entityType: 'expense',
        vendor: vendorKeyForValidation,
        paymentMethod: paymentMethodKey ?? null,
      });

      if (!parsedExpense.success) {
        setErrors((errors) => ({ ...errors, ...parsedExpense.error.formErrors.fieldErrors }));
        return;
      }

      let vendorKey = expense.vendor;
      if (newVendorName) {
        vendorKey = Data.vendors.create({ value: { name: newVendorName } });
      }

      let paymentMethodKeyToSave = paymentMethodKey ?? null;
      const pmUserID = currentUser()?.uid;
      if (!paymentMethodKeyToSave && newPaymentMethodName) {
        if (!pmUserID) {
          setErrors((errors) => ({
            ...errors,
            paymentMethod: ['Unable to create payment method. Please try again.'],
          }));
          return;
        }

        const loadedLogo =
          loadedExpense?.entityType === 'reviewExpense'
            ? loadedExpense.suggestion.paymentMethod?.logo
            : undefined;

        paymentMethodKeyToSave =
          Data.paymentMethods.create({
            value: {
              name: newPaymentMethodName,
              institution: newPaymentMethodName,
              type: 'other',
              userID: pmUserID,
              digits: '',
              isEditable: true,
              logo: loadedLogo,
            },
          }) ?? null;
      }

      // Reimbursable Default (EZ-5335): email items are never Card Purchases, so it always applies.
      const resolvedExpenseKey = Data.expenses.create(
        {
          ...parsedExpense.data,
          ...getDefaultReimbursableFields(workspaceSettings),
          sourceType: 'email',
          vendor: vendorKey,
          paymentMethod: paymentMethodKeyToSave,
        },
        undefined,
        undefined,
        'review',
      );

      const createdExpensePath = getExpensePath(
        useWorkspaceKeysStore.getState().currentOrganizationKey,
        useWorkspaceKeysStore.getState().currentWorkspaceKey,
        resolvedExpenseKey,
      );
      archiveReviewTransaction(reviewExpense, createdExpensePath);
      next();

      if (resolvedExpenseKey) {
        const oldReports = getCachedReports(resolvedExpenseKey).map((r) => r.key) ?? [];
        const reportsToRemove = oldReports.filter((r) => !reportKeys.includes(r));

        updateReportsEntities({
          reportKeys,
          entityType: 'expenses',
          entityKeys: [resolvedExpenseKey],
        });

        updateReportsEntities({
          reportKeys: reportsToRemove,
          entityType: 'expenses',
          entityKeys: [resolvedExpenseKey],
          remove: true,
        });
      }
    } catch (err) {
      console.error('error saving expense ', err);
      alert('Error saving expense. Please contact support.');
    }
  }

  const onSave = () => handleMeteredReview(saveReviewedTransaction);

  function onDelete() {
    if (loadedExpense) {
      Analytics.track('transaction_reviewed', {
        ...toTransactionSnapshot(loadedExpense),
        action: 'deleted',
        review_source: reviewSourceFor(loadedExpense),
        review_surface: 'web',
      });
    }
    archiveReviewTransaction(reviewExpense, null);
    next();
  }

  if (!loadedExpense) {
    return (
      <Layout.Column style={{ width: '100%', height: '100%' }} center>
        <OpenSans.Primary size={24} weight="bold-700">
          {getTranslation('All Emails Reviewed')}
        </OpenSans.Primary>
        <Spacer.Vertical size={32} />
        <Button.Primary
          onClick={() => navigate('/transactions')}
          content={getTranslation('Continue')}
        />
      </Layout.Column>
    );
  }

  return (
    <Layout.Column style={{ width: '100%', height: '100%' }}>
      {loading ? (
        <Layout.Column
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'fixed',
            zIndex: zIndex.LoadingOverlay,
          }}
        >
          <LoadingSpinner />
        </Layout.Column>
      ) : null}
      <Layout.Row style={{ height: '100%', width: '100%' }}>
        {screenWidth > 800 ? (
          <Layout.Column
            style={{ minWidth: 600, maxWidth: 800, height: '100vh' }}
            grow
            className="hide-on-mobile"
          >
            <ReceiptViewer
              receipts={expense.receipts ?? []}
              onChange={(receipts: string[]) => {
                setExpense((expense) => ({ ...expense, receipts }));
              }}
              selectedReceiptIndex={selectedReceiptIndex}
              setSelectedReceiptIndex={setSelectedReceiptIndex}
              setLoading={setLoading}
            />
          </Layout.Column>
        ) : null}

        <Layout.Column
          grow
          style={{
            height: '100vh',
            boxShadow: '-8px 0 8px rgba(31, 31, 31, 0.1)',
          }}
        >
          <Layout.Column style={{ overflow: 'scroll' }} grow py={24} px={32}>
            <Layout.Row>
              <Spacer.Flex style={{ maxWidth: 150 }} />
              <Layout.Column style={{ width: 421 }}>
                <Spacer.Vertical size={32} />

                <InsertDropdown
                  header={getTranslation('Vendor')}
                  placeholder={getTranslation('Vendor Name')}
                  selectedKey={expense.vendor}
                  autoCompleteValues={[
                    ...allVendors,
                    {
                      key: 'newVendor',
                      value: {
                        name: newVendorName ?? '',
                        iconicIcon: 'add-circle-outline',
                      },
                    },
                  ]}
                  onSelect={(v) => {
                    vendorUserSelectedRef.current = true;
                    const existingVendor = allVendors.find((vendor) => vendor.key === v?.key);
                    if (v?.key && existingVendor) {
                      const vendorCategory = existingVendor.value.category;
                      setExpense((expense) => ({
                        ...expense,
                        vendor: v.key,
                        category: vendorCategory || expense.category,
                      }));
                      setNewVendorName(undefined);
                    } else if (v?.value?.name) {
                      setNewVendorName(v.value.name);
                    }
                  }}
                  error={!!errors?.vendor}
                  iconicIcon="storefront-outline"
                  clearOnSelect={true}
                />

                <Spacer.Vertical size={'s-16'} />

                <SearchDropdown
                  header={getTranslation('Category')}
                  placeholder={getTranslation('Category Name')}
                  selectedKey={expense.category}
                  autoCompleteValues={categoryOptions}
                  onSelect={(c) => {
                    categoryUserSelectedRef.current = true;
                    if (c?.key) {
                      const tipEligible = isTipEligibleCategoryKey(c.key);
                      setExpense((expense) => ({
                        ...expense,
                        category: c?.key,
                        tip: tipEligible ? expense.tip : null,
                      }));
                    }
                  }}
                  error={!!errors?.category}
                  iconicIcon="pricetags-outline"
                  clearOnSelect={true}
                />

                <Spacer.Vertical size={'s-16'} />

                <Layout.Row justify>
                  <Layout.Column style={{ width: taxRate ? '50%' : '100%', paddingRight: '8px' }}>
                    <CurrencyField
                      error={!!errors?.total}
                      onChange={(total) => {
                        setExpense((expense) => ({ ...expense, total }));
                      }}
                      initialValue={expense.total}
                      label={
                        getTranslation('Total') +
                        ((expense.total ?? 0) < 0 ? ' (' + getTranslation('refund') + ')' : '')
                      }
                      updatedValue={updatedTotal}
                      allowNegative
                    />
                  </Layout.Column>
                  {taxRate === undefined ? null : (
                    <Layout.Column style={{ width: '50%', paddingLeft: '8px' }}>
                      <CurrencyField
                        error={!!errors?.tax}
                        onChange={(tax) => setExpense((expense) => ({ ...expense, tax }))}
                        initialValue={expense?.tax ?? undefined}
                        label={taxRate.name}
                        updatedValue={updatedTax}
                      />
                    </Layout.Column>
                  )}
                  {secondTaxRate === undefined ? null : (
                    <Layout.Column style={{ width: '50%', paddingLeft: '8px' }}>
                      <CurrencyField
                        error={!!errors?.secondTaxRate}
                        onChange={(secondTax) =>
                          setExpense((expense) => ({
                            ...expense,
                            secondTax,
                          }))
                        }
                        initialValue={expense?.secondTax ?? undefined}
                        label={secondTaxRate.name}
                        updatedValue={updatedSecondTax}
                      />
                    </Layout.Column>
                  )}
                </Layout.Row>

                {showTipField ? (
                  <>
                    <Spacer.Vertical size={'s-16'} />
                    <TipField
                      tip={expense.tip}
                      onChange={(tip) => setExpense((expense) => ({ ...expense, tip }))}
                    />
                  </>
                ) : null}

                <Spacer.Vertical size={'s-16'} />

                <SearchDropdown
                  header={getTranslation('Payment Method')}
                  placeholder={getTranslation('Payment Method')}
                  selectedKey={paymentMethodKey ?? newPaymentMethodName}
                  autoCompleteValues={[
                    ...paymentMethodOptions,
                    ...(newPaymentMethodName
                      ? [
                          {
                            key: newPaymentMethodName,
                            value: {
                              name: newPaymentMethodName,
                              iconicIcon: 'add-circle-outline',
                            },
                          },
                        ]
                      : []),
                  ]}
                  onSelect={(pm) => {
                    pmUserSelectedRef.current = true;
                    if (pm?.key && paymentMethods.find((p) => p.key === pm.key)) {
                      setPaymentMethodKey(pm.key);
                      setNewPaymentMethodName(undefined);
                    } else if (pm?.value?.name) {
                      setPaymentMethodKey(undefined);
                      setNewPaymentMethodName(pm.value.name);
                    } else {
                      setPaymentMethodKey(undefined);
                      setNewPaymentMethodName(undefined);
                    }
                  }}
                  iconicIcon="card-outline"
                  clearOnSelect={true}
                  allowCreateNew={true}
                  error={!!errors?.paymentMethod}
                />

                <Spacer.Vertical size={'s-16'} />

                <DateField
                  error={!!errors?.date}
                  initialDate={expense.date}
                  disabled={loading}
                  onChange={(date) => setExpense((expense) => ({ ...expense, date }))}
                />

                {workspaceSettings?.showTransactionID ? (
                  <>
                    <Spacer.Vertical size={'s-16'} />

                    <TransactionNumberField
                      transactionID={expense.transactionID}
                      onChange={(transactionID) =>
                        setExpense((expense) => ({ ...expense, transactionID }))
                      }
                    />
                  </>
                ) : null}

                <Spacer.Vertical size={'s-16'} />

                <ReportsField
                  expenseKey={expense.key}
                  onChange={(rks) => setReportKeys([...rks])}
                />

                <Spacer.Vertical size={'s-16'} />

                <NotesField
                  desc={expense.desc}
                  onChange={(notes) => setExpense((expense) => ({ ...expense, desc: notes }))}
                />

                <Spacer.Vertical size={'s-16'} />

                <ReceiptsField
                  receipts={expense.receipts ?? []}
                  onChange={(receipts) => setExpense((expense) => ({ ...expense, receipts }))}
                  selectedReceiptIndex={selectedReceiptIndex}
                  setSelectedReceiptIndex={setSelectedReceiptIndex}
                  setLoading={setLoading}
                  setExpense={() => {}}
                />

                <Spacer.Vertical size={100} />
              </Layout.Column>
              <Spacer.Flex />
            </Layout.Row>
          </Layout.Column>

          <Layout.Row
            style={{
              borderTop: `2px solid ${theme.colors.grayXLight}`,
            }}
            bg="white"
            px={32}
            py={24}
          >
            <Layout.PressableRow
              px
              py={8}
              bg="destructiveXLight"
              radius={100}
              border={[2, 'solid', 'white']}
              align
              onClick={onDelete}
            >
              <Icon name="trash-outline" size={20} color={theme.colors.destructiveDark} />
              <Spacer.Horizontal size={8} />
              <OpenSans.Primary color="destructiveDark" grow size="s-16">
                {getTranslation('Delete Expense')}
              </OpenSans.Primary>
              <Spacer.Horizontal size={4} />
            </Layout.PressableRow>

            <Spacer.Horizontal size={'m-18'} />

            <Button.Primary
              onClick={onSave}
              radius={50}
              style={{
                border: '2px solid #fff',
                boxShadow: '0 0 8px rgba(255, 255, 255, .5)',
              }}
            >
              <OpenSans.Custom
                size={15}
                weight="bold-700"
                style={{ color: theme.colors.buttonWhite }}
              >
                {getTranslation('Save Expense')}
              </OpenSans.Custom>
              <Icon
                size={15}
                color={theme.colors.buttonWhite}
                style={{ paddingLeft: 10 }}
                name="chevron-forward"
              />
            </Button.Primary>
          </Layout.Row>
        </Layout.Column>
      </Layout.Row>

      <CloseButton onClose={() => navigate(`/transactions`)} />
    </Layout.Column>
  );
};
